import { useCallback, useContext } from 'react'
import {
  itemsSuccessFetch, itemsFailedFetch, itemsLodingFetch, itemsAdd, itemsDelete, itemsUpdate
} from '../actions/itemsActions'
import { AppContext } from '../context/AppProvider'
import { buildPrivateApiUrl } from '../config/api'

const normalizeArray = (payload) => (Array.isArray(payload) ? payload : [payload])

export const useItems = () => {
  const { dispatch } = useContext(AppContext)

  const apiUrl = buildPrivateApiUrl('items')

  const searchItems = useCallback(async (searchTerm = '') => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }

      try {
        dispatch(itemsLodingFetch())
        const response = await fetch(`${apiUrl}?search=${searchTerm}`, {
          headers: { ...headers, Authorization: `${token}` }
        })
        const data = await response.json()
        dispatch(itemsSuccessFetch(data))
        return data.data
      } catch (error) {
        dispatch(itemsFailedFetch(error))
        console.error('Failed to fetch items:', error)
        return []
      }
    }
    return []
  }, [apiUrl, dispatch])

  const exportItems = useCallback(async (filters = {}) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }

      try {
        const response = await fetch(`${apiUrl}/export`, {
          method: 'POST',
          headers: { ...headers, Authorization: `${token}` },
          body: JSON.stringify({ data: filters })
        })
        const { url } = await response.json()
        return url
      } catch (error) {
        console.error('Failed to fetch items:', error)
        return []
      }
    }
    return []
  }, [apiUrl])

  const fetchItemsByEntityType = useCallback(async (queryParam, value, filters) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      let url = apiUrl
      let first = true
      if (queryParam !== null && value !== null) {
        first = false
        url = `${apiUrl}?${queryParam}=${value}`
      }

      if (filters && typeof filters === 'object' && Object.keys(filters).length > 0) {
        Object.entries(filters).forEach(([key, currentValue]) => {
          url += first
            ? '?' + `${encodeURIComponent(key)}=${encodeURIComponent(currentValue)}`
            : '&' + `${encodeURIComponent(key)}=${encodeURIComponent(currentValue)}`
          first = false
        })
      }
      dispatch(itemsLodingFetch())
      try {
        const response = await fetch(`${url}`, {
          headers: { ...headers, Authorization: `${token}` }
        })
        const data = await response.json()
        dispatch(itemsSuccessFetch(data))
      } catch (error) {
        console.error('Failed to fetch items:', error)
        dispatch(itemsFailedFetch(error))
      }
    }
  }, [apiUrl, dispatch])

  const addItems = useCallback(async (payload) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ data: normalizeArray(payload) })
        })
        if (response.ok) {
          const { data } = await response.json()
          normalizeArray(data).forEach(item => dispatch(itemsAdd(item)))
          return data
        }
        console.error('Error with status code:', response.status)
      } catch (error) {
        console.error('Add items error:', error)
      }
    }
    return []
  }, [apiUrl, dispatch])

  const updateItems = useCallback(async (payload) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      try {
        const updates = normalizeArray(payload)
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ data: updates })
        })
        if (response.ok) {
          const payloadData = await response.json()
          const updatedItems = normalizeArray(payloadData.data || [])
          updatedItems.forEach(item => dispatch(itemsUpdate(item)))
          return {
            data: updatedItems,
            errors: normalizeArray(payloadData.errors || []).filter(Boolean)
          }
        }
        console.error('Error with status code:', response.status)
      } catch (error) {
        console.error('Update items error:', error)
      }
    }
    return { data: [], errors: [] }
  }, [apiUrl, dispatch])

  const deleteItems = useCallback(async (items) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      try {
        const ids = normalizeArray(items).map(item => (typeof item === 'string' ? item : item._id))
        const response = await fetch(apiUrl, {
          method: 'DELETE',
          headers,
          body: JSON.stringify({ data: ids })
        })
        if (response.ok) {
          const payloadData = await response.json()
          ids.forEach(id => dispatch(itemsDelete(id)))
          return {
            ids,
            statusCode: payloadData.statusCode ?? 200,
            message: payloadData.message ?? '',
            error: payloadData.error ?? null
          }
        }
        console.error('Error with status code:', response.status)
      } catch (error) {
        console.error('Delete items error:', error)
      }
    }
    return { ids: [], statusCode: 500, message: '', error: 'Unauthorized' }
  }, [apiUrl, dispatch])

  return { fetchItemsByEntityType, addItems, updateItems, deleteItems, searchItems, exportItems }
}
